// src/pages/TutorPremiumPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaCheck } from "react-icons/fa";
import { fetchTutorStatus, processPremiumPayment } from "../utils/premiumService";
import { notificationService } from "../utils/notificationService";
import mascotImg from "../assets/App-logo-final.png";
import "./TutorPremiumPage.css";

const BENEFITS = [
  "Chat with your octopus tutor, up to 100 messages a day",
  "Hints first, so you actually learn instead of copying",
  "Keeps your goal in mind and ties lessons to your dream",
  "Physics, chemistry and biology explained step by step",
];

const TutorPremiumPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // /tutor/premium?renew=1 lets a user with days left add 30 more
  const allowRenew = new URLSearchParams(location.search).get("renew") === "1";

  const [info, setInfo] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | opening | confirming | slow
  const alive = useRef(true);
  const untilBefore = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadStatus = useCallback(async () => {
    const s = await fetchTutorStatus();
    if (alive.current) setInfo(s);
    return s;
  }, []);

  // First load: active users go straight to the chat
  const firstLoad = useCallback(async () => {
    setLoadFailed(false);
    try {
      const s = await loadStatus();
      if (s.status === "active" && !allowRenew) {
        navigate("/tutor", { replace: true });
      }
    } catch (e) {
      console.error("Could not load tutor status:", e);
      if (alive.current) setLoadFailed(true);
    }
  }, [loadStatus, allowRenew, navigate]);

  useEffect(() => {
    firstLoad();
  }, [firstLoad]);

  // After payment: wait until the server (via Razorpay's webhook) switches premium on
  const waitForPremium = useCallback(async () => {
    setPhase("confirming");
    for (let i = 0; i < 30 && alive.current; i++) {
      try {
        const s = await loadStatus();
        if (s.status === "active" && s.premiumUntilMs > untilBefore.current) {
          navigate("/tutor", { replace: true });
          return;
        }
      } catch (e) {
        console.error("Status check failed:", e);
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    if (alive.current) setPhase("slow");
  }, [loadStatus, navigate]);

  const handlePay = async () => {
    if (phase !== "idle" || !info) return;
    untilBefore.current = info.premiumUntilMs || 0;
    setPhase("opening");
    await processPremiumPayment(
      () => {
        waitForPremium();
      },
      (err) => {
        if (!alive.current) return;
        setPhase("idle");
        if (err && /cancelled/i.test(err.message || "")) {
          notificationService.showWarning("Payment cancelled");
        } else {
          notificationService.showError("Payment didn't go through. Please try again.");
        }
      }
    );
  };

  // ---- Wording for the three situations ----
  const isExpired = info?.status === "expired";
  const isEarlyRenew = info?.status === "active" && allowRenew;
  const endedOn = info?.premiumUntilMs
    ? new Date(info.premiumUntilMs).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  let heading = "Meet your science buddy";
  let subline = "A friendly octopus tutor that helps you reach your dream.";
  let buttonLabel = "Unlock for ₹189";
  if (isExpired) {
    heading = "Your month is up";
    subline = `Your plan ended on ${endedOn}. Renew to keep learning with your octopus buddy.`;
    buttonLabel = "Renew for ₹189";
  } else if (isEarlyRenew) {
    heading = "Add 30 more days";
    subline = `You have ${info.daysLeft} day${info.daysLeft === 1 ? "" : "s"} left. Renewing adds 30 days on top.`;
    buttonLabel = "Renew for ₹189";
  }

  if (phase === "opening") buttonLabel = "Opening payment...";
  if (phase === "confirming") buttonLabel = "Confirming your payment...";

  const busy = phase === "opening" || phase === "confirming";

  return (
    <div className="tp-page">
      <button className="tp-back" onClick={() => navigate(-1)} aria-label="Go back">
        <FaArrowLeft />
      </button>

      <img src={mascotImg} alt="Your octopus buddy" className="tp-mascot" />

      {loadFailed ? (
        <div className="tp-center">
          <p className="tp-sub">We couldn't load your plan. Check your connection and try again.</p>
          <button className="tp-btn" onClick={firstLoad}>
            Try again
          </button>
        </div>
      ) : !info ? (
        <p className="tp-sub tp-center">Loading...</p>
      ) : (
        <>
          <h1 className="tp-title">{heading}</h1>
          <p className="tp-sub">{subline}</p>

          <ul className="tp-benefits">
            {BENEFITS.map((text) => (
              <li key={text}>
                <span className="tp-check" aria-hidden="true">
                  <FaCheck />
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ul>

          <div className="tp-price">
            <span className="tp-amount">₹189</span>
            <span className="tp-term">for 30 days · one-time payment, no auto-renewal</span>
          </div>

          <button className="tp-btn" onClick={handlePay} disabled={busy}>
            {buttonLabel}
          </button>

          {phase === "confirming" && (
            <p className="tp-note">Please keep this screen open for a few seconds.</p>
          )}

          {phase === "slow" && (
            <div className="tp-note">
              <p>
                Your payment is taking longer than usual to confirm. If money was
                deducted, your plan will switch on shortly.
              </p>
              <button className="tp-link" onClick={waitForPremium}>
                Check again
              </button>
            </div>
          )}

          {!busy && phase !== "slow" && (
            <button className="tp-link" onClick={() => navigate(-1)}>
              Maybe later
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default TutorPremiumPage;